// Page-local wizard state: a useReducer behind a small Context, so it resets
// when the user leaves the page (no global store needed).

import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { TEACHER_STEPS } from "./types";
import type {
  TeacherAnswers,
  TeacherOnboardingState,
  TeacherStep,
} from "./types";

const initialAnswers: TeacherAnswers = {
  voice1: null,
  voice2: null,
  illustration: null,
  structure: null,
  depth: 50,
  autonomy: 50,
  guardrail: "",
};

const initialState: TeacherOnboardingState = {
  answers: initialAnswers,
  currentStep: "welcome",
};

type Action =
  | { type: "SET_ANSWER"; value: Partial<TeacherAnswers> }
  | { type: "GO_FORWARD" }
  | { type: "GO_BACK" };

const stepIndex = (step: TeacherStep): number => TEACHER_STEPS.indexOf(step);

// Returns the step at a clamped index, falling back to the current step.
const stepAt = (index: number, fallback: TeacherStep): TeacherStep =>
  TEACHER_STEPS[index] ?? fallback;

const reducer = (
  state: TeacherOnboardingState,
  action: Action
): TeacherOnboardingState => {
  switch (action.type) {
    case "SET_ANSWER": {
      const answers = { ...state.answers, ...action.value };
      // voice2 is adaptive to voice1; if voice1 changes, the old voice2 no
      // longer belongs to the chosen branch, so clear it.
      if (
        action.value.voice1 !== undefined &&
        action.value.voice1 !== state.answers.voice1
      ) {
        answers.voice2 = null;
      }
      return { ...state, answers };
    }
    case "GO_FORWARD": {
      const next = Math.min(
        stepIndex(state.currentStep) + 1,
        TEACHER_STEPS.length - 1
      );
      return { ...state, currentStep: stepAt(next, state.currentStep) };
    }
    case "GO_BACK": {
      const prev = Math.max(stepIndex(state.currentStep) - 1, 0);
      return { ...state, currentStep: stepAt(prev, state.currentStep) };
    }
    default:
      return state;
  }
};

interface TeacherOnboardingContextValue extends TeacherOnboardingState {
  /** Commit an answer immediately (used for choices that gate Continue). */
  setAnswer: (value: Partial<TeacherAnswers>) => void;
  /** Buffer an answer in a ref without re-rendering; flushed by commitStaged. */
  stageAnswer: (value: Partial<TeacherAnswers>) => void;
  /** Flush staged answers into state and return the merged answers. */
  commitStaged: () => TeacherAnswers;
  goForward: () => void;
  goBack: () => void;
  stepNumber: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  /** Called by the finish step's CTA; when unset the CTA navigates to the projects list. */
  onComplete?: () => void;
}

const TeacherOnboardingContext =
  createContext<TeacherOnboardingContextValue | null>(null);

export const TeacherOnboardingProvider: React.FC<{
  children: React.ReactNode;
  onComplete?: () => void;
}> = ({ children, onComplete }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Draft answers buffered without re-rendering (slider drags, textarea typing).
  const stagedRef = useRef<Partial<TeacherAnswers>>({});

  const value = useMemo<TeacherOnboardingContextValue>(() => {
    const stepNumber = stepIndex(state.currentStep);
    return {
      ...state,
      setAnswer: (value) => dispatch({ type: "SET_ANSWER", value }),
      stageAnswer: (value) => {
        stagedRef.current = { ...stagedRef.current, ...value };
      },
      commitStaged: () => {
        const staged = stagedRef.current;
        const merged = { ...state.answers, ...staged };
        if (Object.keys(staged).length > 0) {
          dispatch({ type: "SET_ANSWER", value: staged });
          stagedRef.current = {};
        }
        return merged;
      },
      goForward: () => dispatch({ type: "GO_FORWARD" }),
      goBack: () => dispatch({ type: "GO_BACK" }),
      stepNumber,
      isFirstStep: stepNumber === 0,
      isLastStep: stepNumber === TEACHER_STEPS.length - 1,
      onComplete,
    };
  }, [state, onComplete]);

  return (
    <TeacherOnboardingContext.Provider value={value}>
      {children}
    </TeacherOnboardingContext.Provider>
  );
};

export const useTeacherOnboarding = (): TeacherOnboardingContextValue => {
  const ctx = useContext(TeacherOnboardingContext);
  if (!ctx) {
    throw new Error(
      "useTeacherOnboarding must be used within a TeacherOnboardingProvider"
    );
  }
  return ctx;
};
